import {
  Box,
  Button,
  MenuItem,
  IconButton,
  OutlinedInput,
  Select,
  Switch,
  Typography, Checkbox,
  FormControlLabel
} from '@mui/material';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import { Add, Delete } from "@mui/icons-material";
import { FormatAlignLeft, FormatAlignCenter, FormatAlignRight } from "@mui/icons-material";
import { Settings } from "@mui/icons-material";
import styles from './styles';
import { CellConfigDialog } from '../../../../../../components/Dialogs/CellConfig';

import {SolActivePanel} from './history';

export const rangeOptions = [
  "1 Monat",
  "6 Monate",
  "YTD",
  "1 Jahr",
  "3 Jahre",
  "5 Jahre",
  "10 Jahre",
  "Gesamt",
];

function areRangesEqual(current: string[] = [], next: string[] = []) {
  if (current.length !== next.length) {
    return false;
  }

  return current.every((value, index) => value === next[index]);
}

type Props = {
  id?: number;
  isin: string;
  content: any;
  themeColor: string;
  onChange: (id: number | undefined, value: any) => void;
  onChangeDescription: (text: string) => void;
  onChangeChartType: (type: string) => void;
  onChangeChartIsin: (isin: string) => void;
}

export const HistoryChart = ({
  id,
  content,
  themeColor,
  onChange,
  onChangeChartType,
  onChangeChartIsin,
  isin,
}: Props) => {
  const [openTitleConfigDlg, setOpenTitleConfigDlg] = useState(false);

  const handleChangeChartTitle = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(id, { ...content, chartTitle: e.target.value });
  };

  const handleChangeChartText = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(id, { ...content, chartText: e.target.value });
  };

  const handleChangeChartTextStyle = (align: 'left' | 'center' | 'right') => {
    onChange(id, { ...content, chartTextStyle: align });
  };

  const handleChangeAxisType = (isPercent) => {
    onChange(id, { ...content, isPercent });
  }

  const handleAddAdditionalIsin = () => {
    onChange(id, {
      ...content,
      additionalIsins: [
        ...(content.additionalIsins || []),
        {
          id: +new Date(),
          isin: '',
          title: '',
          description: '',
          color: '',
        }
      ],
    });
  };

  const handleChangeAdditionalIsinData = (index) => (e) => {
    const additionalIsins = content.additionalIsins;
    additionalIsins[index][e.target.name] = e.target.value;
    onChange(id, {
      ...content,
      additionalIsins,
    });
  };

  const handleDeleteAdditionalIsin = (index) => {
    const additionalIsins = content.additionalIsins;
    additionalIsins.splice(index, 1);
    onChange(id, {
      ...content,
      additionalIsins,
    });
  };

  const handleChangeChartType = (e) => {
    onChangeChartType(e.target.value);
  };

  const handleChangeIsin = (e) => {
    onChangeChartIsin(e.target.value);
  };

  const handleChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(id, { ...content, title: e.target.value });
  };

  const handleChangeAllowedRange = (e) => {
    if (content?.isAutoRange) {
      return;
    }

    onChange(id, { ...content, ranges: e.target.value });
  };

  const handleToggleAutoRange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(id, { ...content, isAutoRange: event.target.checked });
  };

  const handleAutoRangesChange = (ranges: string[]) => {
    if (!content?.isAutoRange) {
      return;
    }

    const currentRanges = content?.ranges || [];

    if (areRangesEqual(currentRanges, ranges)) {
      return;
    }

    onChange(id, { ...content, ranges });
  };

  const handleOpenTitleConfig = () => {
    setOpenTitleConfigDlg(true);
  };

  const handleChangeTitleConfig = (config: any) => {
    onChange(id, { ...content, title: config });
    setOpenTitleConfigDlg(false);
  };

  const handleToggleDynamicTitle = () => {
    onChange(id, {
      ...content,
      title: content.title?.isDynamic
        ? ''
        : {
            isDynamic: true,
            fields: [],
          },
    });
  };

  // @ts-ignore
  return (
    <Box>
      <Typography variant="h5">History Chart</Typography>

      <Box display="flex" alignItems="center" gap="16px">
        <Typography sx={{ width: '70px' }} variant="subtitle1">Title</Typography>
        {content.title?.isDynamic ? (
          <>
            <Box sx={styles.dataConfigInputWrapper}>
              {content.title?.fields?.map((field, index) => (
                <Box key={`${index}th`} sx={(theme) => styles.configField(theme, { type: field.type })}>
                  <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
                    {field.type === 'text' ? field.text : `${field.table || '(table)'}.${field.field || '(field)'}`}
                  </Typography>
                </Box>
              ))}
            </Box>
            <CellConfigDialog
              isin={content.isin}
              config={content.title}
              open={openTitleConfigDlg}
              onClose={() => setOpenTitleConfigDlg(false)}
              onConfirm={handleChangeTitleConfig}
            />
            <IconButton sx={{ mr: 4, width: 32, height: 32 }} onClick={handleOpenTitleConfig}>
              <Settings />
            </IconButton>
          </>
        ) : (
          <OutlinedInput
            sx={{ width: '300px' }}
            name="title"
            type="string"
            value={content.title || ''}
            onChange={handleChangeTitle}
          />
        )}

        <FormControlLabel
          label="Is dynamic?"
          sx={styles.switch}
          control={<Switch checked={content.title?.isDynamic} color="secondary" onChange={handleToggleDynamicTitle} />}
        />
      </Box>

      <Box display="flex" alignItems="center" gap="8px" mt={16}>
        <Typography sx={{ width: '70px' }} variant="subtitle1">Ranges: </Typography>
        <Select
          name="chartType"
          sx={styles.chartTypeSelect}
          multiple
          renderValue={(value) => value?.length ? `${value.length > 3 ? `${value.slice(0, 2).join(', ')}, + ${value.length - 2} more` : value.join(', ')}` : 'Not set'}
          value={content?.ranges || []}
          onChange={handleChangeAllowedRange}
          disabled={Boolean(content?.isAutoRange)}
        >
          {rangeOptions.map((option) => (
            <MenuItem key={option} value={option}>
              <Checkbox checked={(content?.ranges || []).indexOf(option) > -1} />
              {option}
            </MenuItem>
          ))}
        </Select>
        <FormControlLabel
          control={(
            <Switch
              checked={Boolean(content?.isAutoRange)}
              color="secondary"
              onChange={handleToggleAutoRange}
            />
          )}
          label="Auto ranges"
          sx={{ ml: 2 }}
        />
      </Box>

      <Box mt={16} display="flex" alignItems="center" gap={16}>
        <Box>
          <OutlinedInput value={content?.isin || ""} onChange={handleChangeIsin} placeholder="ISIN" />
        </Box>
        <Box>
          <Select sx={styles.chartTypeSelect} value={content?.chartType} onChange={handleChangeChartType}>
            <MenuItem value="SolActive">Solactive</MenuItem>
            <MenuItem value="Axxion">Axxion</MenuItem>
          </Select>
        </Box>
        <Box sx={(theme) => styles.axisSwitch(theme, content.isPercent)}>
          <Typography variant="body2" onClick={() => handleChangeAxisType(false)}>
            Point
          </Typography>
          <Switch
            checked={content.isPercent}
            onChange={(e) => handleChangeAxisType(e.target.checked)}
          />
          <Typography variant="body2" color="secondary" onClick={() => handleChangeAxisType(true)}>
            Percent
          </Typography>
        </Box>
      </Box>

      <SolActivePanel
        color={themeColor || '#003F7A'}
        content={content}
        isin={isin}
        onAutoRangesChange={handleAutoRangesChange}
      />

      <Box sx={styles.title}>
        <Typography variant="subtitle2">Title</Typography>
        <input value={content.chartTitle} onChange={handleChangeChartTitle} />
      </Box>
      <Box sx={styles.description}>
        <Box display="flex" alignItems="center" gap={8} mb={2}>
          <Typography variant="subtitle2">Description</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={content.chartTextStyle || 'left'}
            onChange={(e, v) => { if (v) handleChangeChartTextStyle(v); }}
          >
            <ToggleButton value="left">
              <FormatAlignLeft sx={{ width: 20, height: 20 }} />
            </ToggleButton>
            <ToggleButton value="center">
              <FormatAlignCenter sx={{ width: 20, height: 20 }} />
            </ToggleButton>
            <ToggleButton value="right">
              <FormatAlignRight sx={{ width: 20, height: 20 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <textarea style={{ textAlign: (content.chartTextStyle || 'left') as any }} value={content.chartText} onChange={handleChangeChartText} />
      </Box>

      {
        content?.additionalIsins?.map((additionalIsin, index) => (
          <Box sx={styles.additionalIsinPanel}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h5">Additional ISIN #{index + 1}</Typography>
              <IconButton sx={{ width: 32, height: 32, border: 0 }} onClick={() => handleDeleteAdditionalIsin(index)}>
                <Delete sx={{ width: 20, height: 20 }} />
              </IconButton>
            </Box>
            {/*<Box sx={styles.colorPicker}>*/}
            {/*  <HexColorPicker color={additionalIsin.color} onChange={handleChangeIsinColor(index)} />*/}
            {/*</Box>*/}

            <Box display="flex">
              <Box>
                <OutlinedInput name="isin" value={additionalIsin?.isin || ""} onChange={handleChangeAdditionalIsinData(index)} placeholder="ISIN" />
              </Box>
              <Box>
                <Select name="chartType" sx={styles.chartTypeSelect} value={additionalIsin?.chartType} onChange={handleChangeAdditionalIsinData(index)}>
                  <MenuItem value="SolActive">Solactive</MenuItem>
                  <MenuItem value="Axxion">Axxion</MenuItem>
                </Select>
              </Box>
            </Box>

            <Box sx={styles.title}>
              <Typography variant="subtitle2">Title</Typography>
              <input name="title" value={additionalIsin.title} onChange={handleChangeAdditionalIsinData(index)} />
            </Box>
            <Box sx={styles.description}>
              <Typography variant="subtitle2">Description</Typography>
              <textarea name="chartText" value={additionalIsin.chartText} onChange={handleChangeAdditionalIsinData(index)} />
            </Box>
          </Box>
        ))
      }

      <Button variant="outlined" sx={{ mt: 16 }} startIcon={<Add />} onClick={handleAddAdditionalIsin}>
        Additional ISINs
      </Button>
    </Box>
  )
};
